// uuidUtils.js
// Cross-browser UUID generation utility

/**
 * Generate a UUID v4 compatible with all browsers including Safari
 * @returns {string} A UUID v4 string
 */
export const generateUUID = () => {
  // Check if crypto.randomUUID is available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    try {
      return crypto.randomUUID();
    } catch (error) {
      console.warn('crypto.randomUUID failed, falling back to custom implementation:', error);
    }
  }
  
  // Fallback implementation for Safari and older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Generate a UUID using crypto.getRandomValues if available (better randomness)
 * @returns {string} A UUID v4 string
 */
export const generateSecureUUID = () => {
  // Check if crypto.getRandomValues is available
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    try {
      // Use crypto.getRandomValues for better randomness
      const array = new Uint8Array(16);
      crypto.getRandomValues(array);
      
      // Set version (4) and variant bits
      array[6] = (array[6] & 0x0f) | 0x40; // Version 4
      array[8] = (array[8] & 0x3f) | 0x80; // Variant bits
      
      // Convert to hex string
      const hex = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
      
      // Format as UUID
      return [
        hex.substring(0, 8),
        hex.substring(8, 12),
        hex.substring(12, 16),
        hex.substring(16, 20),
        hex.substring(20, 32)
      ].join('-');
    } catch (error) {
      console.warn('crypto.getRandomValues failed, falling back to Math.random:', error);
    }
  }
  
  // Final fallback to Math.random
  return generateUUID();
};

// Default export for convenience
export default generateSecureUUID;
